import { useCallback, useMemo, useState } from 'react';

/**
 * UI hook to track pending (unconfirmed) per-field edits.
 * This is intentionally UI-only (no API calls).
 *
 * Keys are expected to be stable composite strings like:
 * `${formId}_${subformId}_${fieldId}`
 */
const usePendingFieldEdits = () => {
  const [pendingKeys, setPendingKeys] = useState(() => new Set());

  const markPending = useCallback((key) => {
    if (!key) return;
    setPendingKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const markResolved = useCallback((key) => {
    if (!key) return;
    setPendingKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setPendingKeys(new Set());
  }, []);

  const hasPending = pendingKeys.size > 0;

  const pendingCount = pendingKeys.size;

  const pendingKeysArray = useMemo(
    () => Array.from(pendingKeys),
    [pendingKeys]
  );

  return {
    pendingKeys,
    pendingKeysArray,
    pendingCount,
    hasPending,
    markPending,
    markResolved,
    clearAll,
  };
};

export default usePendingFieldEdits;
